<template>
  <v-container text-xs-center style="padding: 0 !important">
    <base-toolbar toolbarTitle="Rank Library">
      <template v-slot:toolbarSearch>
        <v-text-field
          cy-data="cy_Search_rank"
          v-model="search"
          append-icon="search"
          label="Search rank"
          single-line
          hide-details
        ></v-text-field>
        <v-divider vertical class="mx-1" inset></v-divider>
        <catValueDropDown
          :outlined="true"
          :catName="'RANK_CLASSIFICATION'"
          v-model="classificationFilter"
          label="Classification"
        />
      </template>

      <template v-slot:toolbarBtn v-if="showBtn">
        <v-btn color="primary" dark @click="openNew">New Rank</v-btn>
      </template>
    </base-toolbar>

    <base-table
      v-if="!loadingTable"
      :tableHeaders="headers"
      :tableItems="filteredRanks"
      :tableLoading="loadingTable"
      :tableSearch="search"
      :isRankTable="true"
    >
      <template v-slot:table-actions="{ actionItem }" v-if="showBtn">
        <v-icon small class="mr-2" @click="edit(actionItem.node)">edit</v-icon>
        <v-icon small @click="deleteRank(actionItem.node.rankId)"
          >delete</v-icon
        >
      </template>
    </base-table>

    <v-dialog
      v-model="dialog"
      :retain-focus="false"
      max-width="500px"
      persistent
      :key="dialogKey"
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field
                  v-model.trim="rankData.rankCode"
                  label="Rank code"
                  :counter="50"
                  :rules="required"
                  required
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model.trim="rankData.rankName"
                  label="Rank name"
                  :rules="nameRules"
                  :counter="50"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <catValueDropDown
                  :outlined="true"
                  :catName="'RANK_CLASSIFICATION'"
                  v-model="rankData.rankClassification"
                  label="Classification"
                />
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="rankData.rankColor"
                  label="Rank color"
                  :append-icon="'water_drop'"
                  @click:append="showColorPicker = true"
                  readonly
                >
                  <template #prepend>
                    <v-avatar size="24" tile>
                      <div
                        :style="{
                          backgroundColor: rankData.rankColor,
                          width: '100%',
                          height: '100%',
                        }"
                      ></div>
                    </v-avatar>
                  </template>
                </v-text-field>

                <v-dialog v-model="showColorPicker" max-width="300px">
                  <v-card>
                    <v-card-title>Pick a Color</v-card-title>
                    <v-card-text>
                      <v-color-picker
                        v-model="rankData.rankColor"
                        flat
                        hide-canvas
                        hide-inputs
                        mode="hexa"
                      />
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn text @click="showColorPicker = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-flex>

              <v-flex xs12>
                <v-textarea
                  v-model.trim="rankData.rankDesc"
                  :counter="150"
                  label="Rank description"
                  :rules="required"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="close">Cancel</v-btn>
          <v-btn color="secondary" text @click="saveRank">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { validationRules } from "@/constants/global";
import BaseToolbar from "@/components/base/BaseToolbar.vue";
import BaseTable from "@/components/base/BaseTable.vue";
import { rankLibHeaders, rankDefaultData } from "@/constants/tabelsData";
import catValueDropDown from "../shared/cat_val_dropdown.vue";

export default {
  name: "RankLib",
  components: { BaseToolbar, BaseTable, catValueDropDown },
  data() {
    return {
      dialog: false,
      dialogKey: 0,
      formValid: true,
      editedIndex: -1,
      loadingTable: true,
      showColorPicker: false,
      search: "",
      classificationFilter: this.$route.query.classification || null,
      isMobile: false,
      nameRules: validationRules.name,
      required: validationRules.required,
      rankData: Object.assign({}, rankDefaultData),
      defaultRankData: Object.assign({}, rankDefaultData),
      headers: rankLibHeaders,
    };
  },
  async created() {
    await this.$store
      .dispatch("allRankLib")
      .then(() => {
        this.loadingTable = false;
      })
      .catch((error) => {
        console.error(error);
        this.loadingTable = false;
      });
  },
  computed: {
    ...mapGetters(["loading", "rankLib", "user"]),
    formTitle() {
      return this.editedIndex === -1 ? "New Rank" : "Edit Rank";
    },
    showBtn() {
      if (this.user) {
        return this.user.role === "Administrator" ? true : false;
      } else {
        return false;
      }
    },

    // 🟢 New Computed Filtered List
    filteredRanks() {
      return this.rankLib.edges.filter(({ node }) => {
        const matchesSearch =
          this.search === "" ||
          node.rankName.toLowerCase().includes(this.search.toLowerCase()) ||
          node.rankCode.toLowerCase().includes(this.search.toLowerCase());
        const matchesClassification =
          !this.classificationFilter ||
          node.rankClassification === this.classificationFilter;
        return matchesSearch && matchesClassification;
      });
    },
  },
  watch: {
    dialog(val) {
      if (!val) this.close();
    },
    classificationFilter(newVal) {
      this.updateQueryParam("classification", newVal);
    },
  },
  methods: {
    ...mapActions(["deleteCurrentRank", "mergeRank"]),
    updateQueryParam(param, value) {
      const query = { ...this.$route.query };
      value ? (query[param] = value) : delete query[param];
      this.$router.replace({ query });
    },
    onResize() {
      this.isMobile = window.innerWidth < 769;
    },
    openNew() {
      this.rankData = Object.assign({}, this.defaultRankData);
      this.editedIndex = -1;
      this.dialogKey += 1;
      this.dialog = true;
    },
    edit(rank) {
      this.editedIndex = this.rankLib.edges.findIndex(
        ({ node }) => node.rankId === rank.rankId
      );
      this.rankData = Object.assign({}, rank);
      this.dialogKey += 1;
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.editedIndex = -1;
      this.rankData = Object.assign({}, this.defaultRankData);
    },
    saveRank() {
      this.loadingTable = true;
      if (this.formValid) {
        const isEdit = this.editedIndex > -1;
        const payload = {
          ...this.rankData,
          updateSource: String(this.user.userName || "test"),
        };

        if (isEdit) {
          payload.rankId = parseInt(this.rankData.rankId, 10);
        }

        this.mergeRank(payload)
          .then(() => {
            this.$toast[isEdit ? "info" : "success"](
              `Rank ${this.rankData.rankName} has been ${
                isEdit ? "updated" : "added"
              }`
            );
            this.close();
          })
          .catch((error) => {
            console.error(error);
            this.close();
          })
          .finally(() => {
            this.loadingTable = false;
          });
      } else {
        this.$toast.error("Please enter valid data");
        this.loadingTable = false;
      }
    },
    deleteRank(rankId) {
      this.loadingTable = true;
      this.deleteCurrentRank({ rankId })
        .then(() => {
          this.loadingTable = false;
          this.$toast.error("Rank has been deleted");
        })
        .catch((error) => {
          console.error(error);
          this.loadingTable = false;
        });
    },
  },
};
</script>

<style>
.theme--light.v-data-table thead tr th {
  background: #58a5f0;
}
</style>
